// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, cloneElement, findDOMNode, Component, AutoClosable } from '../..';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX, Z_INDEX_POPOVER } from '../../constants';
import Popper from '../Popper';
import IconButton from '../IconButton';
import { wrapFunction } from '../utils';

import './Popover.less';

const PLACEMENT_MAP = {
    'top-left': 'top-end',
    'top-right': 'top-start',
    'right-top': 'right-end',
    'right-bottom': 'right-start',
    'bottom-left': 'bottom-end',
    'bottom-right': 'bottom-start',
    'left-top': 'left-end',
    'left-bottom': 'left-start',
};
const ARROW_HEIGHT = 26;

/**
 * Popover component.
 */
class Popover extends Component {
    constructor(props) {
        super(props);

        const controlled = props.visible !== null;
        this.state = {
            controlled,
            show: controlled ? props.visible : props.defaultVisible || false,
            arrowInHeader: false,
        };
    }

    componentDidMount() {
        this.node = findDOMNode(this);
    }

    componentWillReceiveProps(newProps) {
        const controlled = newProps.visible !== null;
        const newState = {
            controlled,
            show: controlled ? newProps.visible : newProps.defaultVisible || false,
        };

        this.setState(newState, () => {
            if (newProps.canCloseOnEscapePress && newState.show) {
                setTimeout(() => this.focus(), 0);
            }
        });
    }

    focus() {
        if (this.popover === null || document.activeElement === null || !this.state.show) {
            return;
        }

        this.prevFocusElement = document.activeElement;

        const isFocusOutside = !this.popover.contains(document.activeElement);
        if (isFocusOutside) {
            const autofocusElement = this.popover.querySelector('[autofocus]');
            const wrapperElement = this.popover;

            if (autofocusElement !== null) {
                autofocusElement.focus();
            } else if (wrapperElement !== null) {
                wrapperElement.focus();
            }
        }
    }

    checkArrowPosition = () => {
        const { title } = this.props;
        const { show, arrowInHeader } = this.state;

        if (!show || !this.popperData) {
            return;
        }

        const { actualPlacement, arrowStyle } = this.popperData;
        const { top } = arrowStyle;
        const [direction] = actualPlacement.split('-');

        let newArrowInHeader = false;

        const directionBottomOrPopoverLowEnough = direction === 'bottom'
            || (top !== null && this.headerBounds && top < this.headerBounds.height - (ARROW_HEIGHT / 2));
        const rightDirection = direction !== 'top' && directionBottomOrPopoverLowEnough;
        if (title && this.headerBounds && rightDirection) {
            newArrowInHeader = true;
        }

        if (arrowInHeader !== newArrowInHeader) {
            this.setState({ arrowInHeader: newArrowInHeader });
        }
    };

    hotkeyHandler = e => {
        if (!e.isDefaultPrevented() && e.key === 'Escape') {
            this.handleClose();
            e.preventDefault();
        }
    };

    handleTargetClick = () => {
        const { show } = this.state;
        const { canCloseOnOutsideClick, canCloseOnEscapePress } = this.props;
        if (show && !canCloseOnOutsideClick) {
            return;
        }

        this.setState({ show: !show }, () => {
            if (!show && canCloseOnEscapePress) {
                setTimeout(() => this.focus(), 0);
            }
        });
    };

    handleClose = () => {
        const { show } = this.state;
        if (!show) {
            return;
        }

        const { onClose, canCloseOnEscapePress } = this.props;
        const isNeedFocus = canCloseOnEscapePress && this.prevFocusElement &&
            (!document.activeElement || this.popover.contains(document.activeElement));

        if (onClose) {
            onClose();
        } else {
            this.setState({ show: false });
        }

        if (isNeedFocus) {
            this.prevFocusElement.focus();
        }
    };

    handleHeaderResize = contentRect => {
        this.headerBounds = contentRect.bounds;
        this.checkArrowPosition();
    };

    handlePopperUpdate = popperData => {
        this.popperData = popperData;
        this.checkArrowPosition();
    };

    render() {
        const {
            baseClassName,
            className,
            visible,
            defaultVisible,
            title,
            placement,
            canCloseOnOutsideClick,
            canCloseOnEscapePress,
            target,
            intent,
            onClose,
            children,
            ...props
        } = this.props;

        const { controlled, show, arrowInHeader } = this.state;
        let behavior;

        if (placement !== 'auto') {
            const [direction] = placement.split('-');

            if (direction === 'top') {
                behavior = ['top', 'bottom', 'right', 'left'];
            } else if (direction === 'right') {
                behavior = ['right', 'left', 'bottom', 'top'];
            } else if (direction === 'bottom') {
                behavior = ['bottom', 'top', 'left', 'right'];
            } else if (direction === 'left') {
                behavior = ['left', 'right', 'top', 'bottom'];
            }
        }

        const close = (
            <IconButton
                className={classNames(
                    `${baseClassName}__close`,
                    title && `${baseClassName}__close--heading`,
                )}
                onClick={this.handleClose}
                icon="cross-mark"
            />
        );

        const heading = title && (
            <Measure onResize={this.handleHeaderResize} bounds>
                {({ measureRef }) => (
                    <div ref={measureRef} className={`${baseClassName}__heading`}>
                        {close}
                        <h4 className={`${baseClassName}__title`}>{title}</h4>
                    </div>
                )}
            </Measure>
        );

        const trigger = controlled ? (
            target
        ) : cloneElement(target, {
            className: classNames(target.props.className, `${baseClassName}__target`),
            onClick: wrapFunction(target.props.onClick, this.handleTargetClick),
        });

        const popperPlacement = PLACEMENT_MAP[placement] || placement;
        const content = (
            <div
                ref={ref => (this.popover = ref)}
                onKeyUp={canCloseOnEscapePress && show ? this.hotkeyHandler : null}
                tabIndex={canCloseOnEscapePress ? 0 : null}
                className={`${baseClassName}__inner`}
            >
                {heading || close}
                <div className={`${baseClassName}__content`}>
                    {children}
                </div>
            </div>
        );

        return (
            <Popper
                target={trigger}
                arrow={(
                    <span
                        className={classNames(
                            `${baseClassName}__arrow`,
                            arrowInHeader && `${baseClassName}__arrow--heading`,
                        )}
                    />
                )}
                placement={popperPlacement}
                onUpdate={this.handlePopperUpdate}
                className={classNames(
                    {
                        [`${baseClassName}--heading`]: title,
                        [`${baseClassName}--intent-${intent}`]: intent,
                    },
                    className
                )}
                baseClassName={baseClassName}
                behavior={behavior}
                zIndex={Z_INDEX_POPOVER}
                show={show}
                {...props}
            >
                {canCloseOnOutsideClick
                    ? <AutoClosable onClose={this.handleClose} ignoreNodes={() => [this.node]}>{content}</AutoClosable>
                    : content
                }
            </Popper>
        );
    }
}

Popover.propTypes = {
    /**
     * The reference element used to position the popover.
     */
    target: PropTypes.element.isRequired,
    /**
     * Popover visibility. For controlled component only.
     */
    visible: PropTypes.bool,
    /**
     * Popover default (on init) visibility. For uncontrolled component only.
     */
    defaultVisible: PropTypes.bool,
    /**
     * Popover title. Header will only be rendered if this prop is defined.
     */
    title: PropTypes.node,
    /**
     * Click handler for close button. If it is not defined then default behavior (hide popover) will be used.
     */
    onClose: PropTypes.func,
    /**
     * Popover placement
     */
    placement: PropTypes.oneOf([
        'auto',
        'top-left',
        'top',
        'top-right',
        'right-top',
        'right',
        'right-bottom',
        'bottom-right',
        'bottom',
        'bottom-left',
        'left-bottom',
        'left',
        'left-top',
    ]),
    /**
     * Indicating if clicking outside the popover should close it.
     */
    canCloseOnOutsideClick: PropTypes.bool,
    /**
     * Indicating if pressing the escape key should close the popover.
     */
    canCloseOnEscapePress: PropTypes.bool,
    /**
     * Content of the Popover
     */
    children: PropTypes.node,
    /**
     * Visual intent color to apply to component.
     */
    intent: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Popover.defaultProps = {
    visible: null,
    defaultVisible: false,
    title: null,
    onClose: null,
    placement: 'auto',
    canCloseOnOutsideClick: true,
    canCloseOnEscapePress: true,
    children: null,
    intent: null,
    className: null,
    baseClassName: `${CLS_PREFIX}popover`,
};

export default Popover;



// WEBPACK FOOTER //
// ./components/Popover/Popover.js